import { useEffect } from 'react';

/**
 * Right-click context menu for project rows (§3.E "RowContextMenu" —
 * `fn.uni_tools.attachContextMenuToList`). Faithful port of the Backbone
 * helper, instantiated in MainView.init with `buttonSelector:
 * '.whiteboard-table-buttons'` (overriding the default `.ctrl-btns`),
 * `rowSelector:'li.project-li'`, `menuSelector:'.dropdown-edit'`,
 * `tableSelector:'.admin-table.first-fixed-list'`, `project=false`.
 *
 * On `contextmenu`:
 *  - if the target is (or is inside) the action-button cluster, do nothing —
 *    right-clicking the action button does NOT open the menu (parity 81),
 *  - find the nearest `.project-li`, find its `.dropdown-edit`, `preventDefault`,
 *  - hide any active menu, add `.activated` to the menu, position it near the
 *    cursor clamped to the viewport (parity 80), `left = posX - rowWidth - 300`,
 *  - raise the row's parent UL via `.z-100`,
 *  - flip the menu UPWARD (`.top`) when there is not enough space below in the
 *    scroll table (parity 80).
 *
 * `document click → hideMenu` removes `.activated`, resets `left`, clears
 * `.z-100` (parity 82). The `.activated` class is what the loaded whiteboard CSS
 * keys the visible state off for the right-click path.
 *
 * @param rootRef element that owns the rows (the `.admin-table.first-fixed-list`
 *   scroll container); listeners attach to it (contextmenu) and to `document`
 *   (click-outside), matching the Backbone attach on `#ksdm-content`.
 * @param enabled toggles attachment.
 */
export function useRowContextMenu(
  rootRef: React.RefObject<HTMLElement>,
  enabled = true
): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return;

    const ROW_SELECTOR = 'li.project-li';
    const MENU_SELECTOR = '.dropdown-edit';
    const BUTTON_SELECTOR = '.whiteboard-table-buttons';
    const TABLE_SELECTOR = '.admin-table.first-fixed-list';

    let activeMenu: HTMLElement | null = null;

    const hideMenu = () => {
      if (activeMenu) {
        activeMenu.classList.remove('activated');
        activeMenu.style.left = '';
        activeMenu = null;
      }
      document.querySelectorAll('.z-100').forEach((el) => el.classList.remove('z-100'));
    };

    const showMenu = (menu: HTMLElement, x: number, y: number, row: HTMLElement) => {
      const { innerWidth, innerHeight } = window;
      menu.classList.add('activated');
      menu.classList.remove('top');
      const rect = menu.getBoundingClientRect();
      const containerWidth = row.offsetWidth;
      let posX = x;
      let posY = y;
      // Prevent viewport overflow.
      if (posX + rect.width > innerWidth) posX -= rect.width;
      if (posY + rect.height > innerHeight) posY -= rect.height;
      // project=false branch: left = posX - rowWidth - 300.
      menu.style.left = `${posX - containerWidth - 300}px`;
      // Raise the row's parent UL above siblings.
      document.querySelectorAll('.z-100').forEach((el) => el.classList.remove('z-100'));
      row.parentElement?.classList.add('z-100');
      // Flip UPWARD when there is not enough space below in the scroll table.
      // Mirrors attachContextMenuToList showMenu exactly:
      //   pos = table.height() - row.getBoundingClientRect().top - table.offset().top
      // (jQuery .height() ≈ clientHeight, .offset().top = rect.top + scrollY).
      const table = (row.closest(TABLE_SELECTOR) ||
        document.querySelector(TABLE_SELECTOR)) as HTMLElement | null;
      if (table) {
        const tableRect = table.getBoundingClientRect();
        const tableOffsetTop = tableRect.top + window.scrollY;
        const pos = table.clientHeight - row.getBoundingClientRect().top - tableOffsetTop;
        const inner = row.querySelector(MENU_SELECTOR) as HTMLElement | null;
        if (inner) {
          if (pos < 0) inner.classList.add('top');
          else inner.classList.remove('top');
        }
      }
    };

    const onContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Right-clicking the action button cluster itself is suppressed (parity 81).
      if (target.closest(BUTTON_SELECTOR)) return;
      const row = (target.closest(ROW_SELECTOR) as HTMLElement | null);
      if (!row || !root.contains(row)) return;
      const menu = row.querySelector(MENU_SELECTOR) as HTMLElement | null;
      if (!menu) return;
      e.preventDefault();
      hideMenu();
      activeMenu = menu;
      showMenu(menu, e.pageX, e.pageY, row);
    };

    const onClickOutside = () => hideMenu();

    root.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('click', onClickOutside);

    return () => {
      root.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('click', onClickOutside);
      hideMenu();
    };
  }, [rootRef, enabled]);
}
